import React, { useCallback, useEffect, useRef } from 'react'

import { useLockScroll } from '@/hooks/useLockScroll'
import { ShopifyStorefrontImage } from '@/types'
import { ProductImageZoomWrapper, ZoomCloseButton } from './styles'
import { ShopifyImage } from '@/components/ShopifyImage'
import { useStatefulRef } from '@/hooks/useStatefulRef'

interface ZoomedImageProps {
  image: ShopifyStorefrontImage
  isSelectedImage: boolean
  containerElement: HTMLDivElement | undefined | null
}

const ZoomedImage: React.FC<ZoomedImageProps> = ({
  image,
  isSelectedImage,
  containerElement,
}) => {
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (!containerElement || !imageRef.current) return
    if (isSelectedImage) {
      containerElement.scrollTo({
        top: Math.max(imageRef.current.offsetTop - 50, 0),
      })
    }
  }, [isSelectedImage, containerElement])

  return (
    <div ref={imageRef}>
      <ShopifyImage image={image} />
    </div>
  )
}

interface ProductImageZoomProps {
  close: () => void
  images: ShopifyStorefrontImage[]
  selectedImageId: string
}

export const ProductImageZoom: React.FC<ProductImageZoomProps> = ({
  close,
  images,
  selectedImageId,
}) => {
  const { lockScroll, unlockScroll } = useLockScroll()
  const containerRef = useStatefulRef<HTMLDivElement>(null)

  const handleEscapePress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    },
    [close],
  )

  useEffect(() => {
    lockScroll()
    window.addEventListener('keyup', handleEscapePress)
    return () => {
      unlockScroll()
      window.removeEventListener('keyup', handleEscapePress)
    }
  }, [handleEscapePress, lockScroll, unlockScroll])

  return (
    <ProductImageZoomWrapper ref={containerRef}>
      <div className="container">
        <ZoomCloseButton onClick={close}>X</ZoomCloseButton>
        {images.map((image) => (
          <ZoomedImage
            key={image.id}
            image={image}
            isSelectedImage={image.id === selectedImageId}
            containerElement={containerRef.current}
          />
        ))}
      </div>
    </ProductImageZoomWrapper>
  )
}
